/**
 * `AccountEditCard` — edit the account's name, status, and the
 * staff-controlled `is_internal` exclusion flag.
 *
 * PATCH /internal/admin/accounts/:id with `{ name?, status?,
 * is_internal? }`. The server validates `status` against the closed
 * enum `active | past_due | suspended | closed` and 400s
 * `{error:'no_fields_to_update'}` if the body is empty.
 *
 * A confirm dialog gates any transition AWAY from `active` —
 * "Disable this account?" — because suspended/closed has merchant-
 * facing consequences (login blocked, mandate engine pauses).
 *
 * `is_internal` (ADR-0140 honest-metrics follow-up) is the founder's
 * lever for marking fully-test accounts so they DON'T pollute the
 * cross-tenant metrics rollup. It is NOT the right knob for the
 * mixed dogfood account that has both staff and real merchant
 * activity — that case is solved upstream by row-level
 * smoke-agent filtering.
 *
 * The PATCH response carries the latest `{ name, status, is_internal }`;
 * the parent passes `onUpdated` so it can refresh the surrounding
 * detail panel without re-issuing GET /accounts/:id.
 */

import { useState } from 'react';

import type {
  AdminAccountDetail,
  AdminAccountUpdateBody,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { ConfirmDialog } from '../../components/atoms/ConfirmDialog.js';
import { Field } from '../../components/atoms/Field.js';
import { useC } from '../../lib/theme.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

type AccountStatus = NonNullable<AdminAccountUpdateBody['status']>;
const STATUS_OPTIONS: readonly AccountStatus[] = [
  'active',
  'past_due',
  'suspended',
  'closed',
];

export interface AccountEditCardProps {
  account: AdminAccountDetail;
  onUpdated: (next: {
    name: string;
    status: string;
    is_internal: boolean;
  }) => void;
}

export function AccountEditCard({
  account,
  onUpdated,
}: AccountEditCardProps): JSX.Element {
  const C = useC();
  const api = useAdminApi();

  const [name, setName] = useState(account.name);
  const [status, setStatus] = useState<AccountStatus>(
    (STATUS_OPTIONS as readonly string[]).includes(account.status)
      ? (account.status as AccountStatus)
      : 'active',
  );
  const [isInternal, setIsInternal] = useState<boolean>(account.is_internal);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successAt, setSuccessAt] = useState<number | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const dirty =
    name.trim() !== account.name ||
    status !== account.status ||
    isInternal !== account.is_internal;
  const disabling = status !== 'active' && account.status === 'active';

  const doSave = async (): Promise<void> => {
    if (!dirty || pending) return;
    setPending(true);
    setError(null);
    try {
      // Build a mutable draft then narrow to the readonly contract on
      // the wire — AdminAccountUpdateBody's fields are `readonly` so
      // the API surface advertises immutability to callers, but we
      // need to conditionally assemble it locally.
      const draft: {
        name?: string;
        status?: AccountStatus;
        is_internal?: boolean;
      } = {};
      if (name.trim() !== account.name) draft.name = name.trim();
      if (status !== account.status) draft.status = status;
      if (isInternal !== account.is_internal) draft.is_internal = isInternal;
      const body: AdminAccountUpdateBody = draft;
      const res = await api.updateAccount(account.account_id, body);
      onUpdated({
        name: res.account.name,
        status: res.account.status,
        is_internal: res.account.is_internal,
      });
      setSuccessAt(Date.now());
    } catch (err) {
      const reason =
        err instanceof ApiError && err.server_reason !== undefined
          ? `: ${err.server_reason}`
          : '';
      setError(`Could not save account changes${reason}.`);
    } finally {
      setPending(false);
      setConfirmOpen(false);
    }
  };

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (disabling) {
      setConfirmOpen(true);
      return;
    }
    void doSave();
  };

  return (
    <Card style={{ padding: 20 }}>
      <div
        style={{
          fontFamily: monoFont,
          fontSize: 10,
          color: C.muted,
          letterSpacing: 2,
          textTransform: 'uppercase',
          marginBottom: 14,
        }}
      >
        Account
      </div>

      <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4">
        <Field label="Name">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-full px-3 py-2 outline-none"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          />
        </Field>

        <Field
          label="Status"
          hint={
            <>
              <code>active</code>: normal. <code>past_due</code>: billing
              attention. <code>suspended</code> = Disable. <code>closed</code>:
              terminal.
            </>
          }
        >
          <select
            value={status}
            onChange={(e) => setStatus(e.target.value as AccountStatus)}
            className="w-full px-3 py-2 outline-none"
            style={{
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              background: C.surface2,
              border: `1px solid ${C.border}`,
              borderRadius: 2,
            }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </select>
        </Field>

        <InternalToggle
          checked={isInternal}
          onChange={setIsInternal}
          disabled={pending}
        />

        <div className="flex items-center justify-between pt-2">
          <div
            style={{
              fontFamily: monoFont,
              fontSize: 10,
              color: C.dim,
              letterSpacing: 1,
            }}
          >
            {account.account_id}
          </div>
          <div className="flex items-center gap-3">
            {error !== null ? (
              <span
                style={{
                  fontFamily: sansFont,
                  fontSize: 12,
                  color: C.coralFg,
                }}
              >
                {error}
              </span>
            ) : successAt !== null && Date.now() - successAt < 4000 ? (
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.limeFg,
                  letterSpacing: 2,
                  textTransform: 'uppercase',
                }}
              >
                Saved
              </span>
            ) : null}
            <button
              type="submit"
              disabled={!dirty || pending}
              style={{
                background: dirty ? C.lime : C.surface2,
                color: dirty ? C.ink : C.muted,
                fontFamily: sansFont,
                fontSize: 13,
                fontWeight: 600,
                padding: '8px 14px',
                borderRadius: 2,
                border: 0,
                opacity: pending ? 0.6 : 1,
              }}
            >
              {pending ? 'Saving…' : 'Save account'}
            </button>
          </div>
        </div>
      </form>

      {account.is_internal !== isInternal ? (
        <div
          style={{
            marginTop: 12,
            padding: '8px 10px',
            background: C.amberDim,
            border: `1px solid rgba(255,182,39,0.3)`,
            borderRadius: 2,
            fontFamily: sansFont,
            fontSize: 11,
            color: C.amberFg,
            lineHeight: 1.5,
          }}
        >
          {isInternal
            ? 'Marking this account as internal will retroactively exclude its activity from cross-tenant metrics (volume, churn, cohorts, geography) on the next rollup.'
            : 'Un-marking this account will let its activity show up in cross-tenant metrics again.'}
        </div>
      ) : null}

      <ConfirmDialog
        open={confirmOpen}
        title={`Disable ${account.name}?`}
        body={
          <>
            Setting status to <code>{status}</code> will block the merchant from
            signing in and pause the mandate engine for their sites. This is
            reversible — set back to <code>active</code> to restore.
          </>
        }
        confirmLabel={status === 'closed' ? 'Close account' : 'Disable'}
        tone="coral"
        pending={pending}
        onConfirm={() => void doSave()}
        onCancel={() => setConfirmOpen(false)}
      />
    </Card>
  );
}

interface InternalToggleProps {
  checked: boolean;
  onChange: (next: boolean) => void;
  disabled?: boolean;
}

/**
 * `InternalToggle` — checkbox + label + helper text for the
 * `is_internal` exclusion flag. Visually styled as a small inline
 * card so it reads as a distinct concern from name/status (the form's
 * other fields are merchant-facing settings; this one is a Sill-
 * internal classification).
 */
function InternalToggle({
  checked,
  onChange,
  disabled,
}: InternalToggleProps): JSX.Element {
  const C = useC();
  return (
    <div
      style={{
        background: C.surface2,
        border: `1px solid ${C.borderSoft}`,
        borderRadius: 2,
        padding: '12px 14px',
      }}
    >
      <label
        className="flex items-start gap-3"
        style={{
          fontFamily: sansFont,
          fontSize: 13,
          color: C.text,
          cursor: disabled === true ? 'not-allowed' : 'pointer',
          lineHeight: 1.5,
        }}
      >
        <input
          type="checkbox"
          checked={checked}
          onChange={(e): void => onChange(e.target.checked)}
          disabled={disabled === true}
          style={{
            marginTop: 2,
            accentColor: C.limeFg,
            flexShrink: 0,
          }}
        />
        <span>
          <span style={{ fontWeight: 500 }}>Internal / test account</span>
          <span
            style={{
              display: 'block',
              fontSize: 11,
              color: C.muted,
              marginTop: 2,
              lineHeight: 1.5,
            }}
          >
            Excluded from investor-facing metrics (volume, accounts, mandates,
            churn, cohorts, geography). Use for fully-internal test accounts
            only — not for the mixed dogfood account.
          </span>
        </span>
      </label>
    </div>
  );
}
